import { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BarChart3 } from "lucide-react"

import { summarizeAverageScoreCard } from "@/components/Copilot/summarizers"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { Evaluator, MergedEvaluationCase } from "./types"

interface AverageScoreCardProps {
  cases: MergedEvaluationCase[]
  selectedEvaluator?: Evaluator
}

const AverageScoreCard = ({
  cases,
  selectedEvaluator,
}: AverageScoreCardProps) => {
  const { t } = useTranslation()
  const [displayScore, setDisplayScore] = useState<number>(0)

  // Calculate average score from results that have scores
  const averageScore = useMemo(() => {
    const scores = cases
      .filter((caseData) => typeof caseData.score === "number")
      .map((caseData) => caseData.score as number)

    if (scores.length === 0) return null

    return scores.reduce((sum, score) => sum + score, 0) / scores.length
  }, [cases])

  // Animate score changes
  useEffect(() => {
    if (averageScore === null) {
      setDisplayScore(0)
      return
    }

    const targetScore = averageScore
    const startScore = displayScore
    const duration = 500 // 500ms animation
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentScore =
        startScore + (targetScore - startScore) * easeOutQuart

      setDisplayScore(currentScore)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [averageScore, displayScore])

  // Calculate statistics
  const stats = useMemo(() => {
    const scores = cases
      .filter((caseData) => typeof caseData.score === "number")
      .map((caseData) => caseData.score as number)

    if (scores.length === 0 || cases.length === 0) return null

    const min = Math.min(...scores)
    const max = Math.max(...scores)
    const completed = scores.length
    const total = cases.length
    const completionRate = total > 0 ? (completed / total) * 100 : 0

    return { min, max, completed, total, completionRate }
  }, [cases])

  if (!selectedEvaluator) return null

  return (
    <div className="mt-6 bg-linear-to-r from-blue-500/10 to-purple-600/10 dark:from-blue-400/20 dark:to-purple-500/20 rounded-lg border border-blue-200/50 dark:border-blue-300/30 p-4 backdrop-blur-xs relative">
      <CopilotHiddenField
        fieldKey="summary.averageScore"
        value={summarizeAverageScoreCard({
          evaluatorName: selectedEvaluator?.name,
          avgScore: averageScore,
          min: stats?.min,
          max: stats?.max,
          completionRate: stats?.completionRate,
          completed: stats?.completed,
          total: stats?.total,
        })}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-blue-500/20 dark:bg-blue-400/30 rounded-md">
            <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t("evaluation.evaluationTable.averageScore.title")}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {selectedEvaluator.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          {stats && (
            <>
              <div className="text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t("evaluation.evaluationTable.averageScore.min")}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {stats.min.toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t("evaluation.evaluationTable.averageScore.max")}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {stats.max.toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t("evaluation.evaluationTable.averageScore.progress")}
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {stats.completionRate.toFixed(0)}%
                </div>
              </div>
            </>
          )}

          <div className="text-right">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {averageScore !== null ? displayScore.toFixed(2) : "0.00"}
              </span>
            </div>
            {stats && (
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {stats.completed}/{stats.total}{" "}
                {t("evaluation.evaluationTable.averageScore.completed")}
              </div>
            )}
          </div>
        </div>
      </div>

      {stats && (
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className="bg-linear-to-r from-blue-500 to-purple-600 h-1.5 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${stats.completionRate}%` }}
          />
        </div>
      )}
    </div>
  )
}

export default AverageScoreCard
